import React, { useEffect } from 'react';
import moment from 'moment';
import * as yup from 'yup';
import Checkbox from '../../../Components/Base/Checkbox/Checkbox';
import DatePicker from '../../../Components/Base/DatePicker/DatePicker';
import Text from '../../../Components/Base/Text/Text';
import TextField from '../../../Components/Base/TextField/TextField';
import SubmitButton from '../../../Components/Button/SubmitButton';
import NumberField from '../../../Components/Form/components/NumberField';
import Select, {
	SelectValueProps,
} from '../../../Components/Form/components/Select';
import FormCondition from '../../../Components/Form/FormCondition';
import FormControl from '../../../Components/Form/FormControl';
import useNotification from '../../../Hooks/useNotification.hook';
import apiClient from '../../../Services/ApiClient';
import ScrollContainer from '../../Base/Components/ScrollContainer';
import { transformPrescriptionData } from '../helpers';
import TermItemSelectField from '../../Term/Components/TermItemSelectField';
import useTranslation from '../../../Hooks/useTranslation';
// @ts-ignore
import styles from './EPrescriptionIssuePrescription.module.scss';
import FormWrapper from '../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputFieldWIthPrefix from '../../../Components/Form/components/InputFieldWithPrefix/InputFieldWIthPrefix';
import PhoneNumberField from '../../../Components/PhoneNumber/PhoneNumberField';
import { useModalContext } from '../../Modal/Services/ModalContext';
import { TreatmentFormModalContextKeys } from '../../../Components/Treatment/TreatmentFormModalContextKeys';
import { usePatientTreatmentContext } from '../../Patient/Components/PatientTreatment/ContextProvider';
import SelectField from '../../../Components/Form/components/SelectField';
import InputWrapper from '../../../Components/Form/components/InputWrapper/InputWrapper';
import { useWorkplaceBasicDataQuery } from '../../../graphql/graphql';
import { useAppContext } from '../../../Services/AppContext';
import { EPrescriptionWizardData } from './EPrescriptionWrapper';

export interface EPrescriptionIssuePrescriptionFormData {
	acute?: boolean;
	adUsumProprium?: boolean;
	dic?: string;
	doctorFirstName?: string;
	doctorLastName?: string;
	doctorPhoneNumber?: number;
	expertise?: SelectValueProps;
	ic?: number;
	icp?: number;
	icz?: number;
	note?: string;
	notification?: SelectValueProps;
	notificationPhoneNumber?: string;
	notificationEmail?: string;
	providerName?: string;
	recommendingDoctorCheck?: boolean;
	repeatAbleCheck?: boolean;
	repeating?: number;
	validTo?: Date;
	international?: boolean;
	workplacePhoneNumber?: number;
	[key: string]: any;
}

interface EPrescriptionPrescriptionInformationProps {
	patientId: string;
	onSuccess?: () => void;
	onLoading?: (isLoading: boolean) => void;
	onPrescriptionCreated: (receiptId: string) => void;
	defaultValues?: Record<string, any>;
	formValues?: EPrescriptionWizardData;
	customOnSubmit?: (values: any) => void;
	createDekurz?: boolean;
}

interface PrescriptionData {
	type: string;
	patientId: string;
	patient?: NonNullable<
		ReturnType<typeof transformPrescriptionData>
	>['patient'];
	sukl: ReturnType<typeof transformPrescriptionData>;
	default: EPrescriptionWizardData;
	caseId?: string;
	workplace?: {
		phone: number;
	};
	createDekurz: boolean;
}

export const ePrescriptionDeliveryOptions = [
	{ label: 'base.onlyToMeddi', value: 'onlyToMEDDI' },
	{ label: 'SMS', value: 'sms' },
	{ label: 'E-mail', value: 'email' },
];

const EPrescriptionIssuePrescription: React.FC<
	EPrescriptionPrescriptionInformationProps
> = ({
	patientId,
	onSuccess,
	onLoading,
	defaultValues,
	formValues,
	onPrescriptionCreated,
	customOnSubmit,
	createDekurz = true,
}) => {
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { registerFormByKey, unregisterFormsByKey } = useModalContext();
	const { caseId } = usePatientTreatmentContext();
	const { workplaceId } = useAppContext();
	const { data: workplaceData } = useWorkplaceBasicDataQuery({
		id: workplaceId,
	});

	const handleSave = async (values: EPrescriptionIssuePrescriptionFormData) => {
		onLoading?.(true);
		unregisterFormsByKey([
			TreatmentFormModalContextKeys.PRESCRIPTION_STEP_1,
			TreatmentFormModalContextKeys.PRESCRIPTION_STEP_2_MEDICAMENT_LIST,
			TreatmentFormModalContextKeys.PRESCRIPTION_STEP_3,
		]);

		const formData: EPrescriptionWizardData = {
			...formValues,
			issuePrescription: values,
		};
		const transformedData = transformPrescriptionData(formData);
		const data: PrescriptionData = {
			type: 'sukl',
			patientId,
			patient: transformedData?.patient,
			sukl: transformedData,
			default: formData,
			caseId: caseId,
			createDekurz,
		};

		if (formData.issuePrescription?.workplacePhoneNumber) {
			data.workplace = {
				phone: formData.issuePrescription.workplacePhoneNumber,
			};
		}

		if (customOnSubmit) {
			customOnSubmit(data);
			onLoading?.(false);
			return;
		}

		try {
			const suklResponse = await apiClient.post('/prescription', data);

			if (!suklResponse?.data?.error) {
				showNotificationSuccess({
					content: t('component.ePrescription.success'),
				});
				onPrescriptionCreated(suklResponse.data.id);
				onSuccess?.();
			}
		} catch (error: any) {
			if (error.response.data.displayableError) {
				showNotificationAlert({
					content: error.response.data.message,
				});
			}
		} finally {
			onLoading?.(false);
		}
	};

	const validationSchema = yup.object({
		acute: yup.boolean().notRequired(),
		adUsumProprium: yup.boolean().notRequired(),
		doctorFirstName: yup.string().when('recommendingDoctorCheck', {
			is: true,
			then: yup
				.string()
				.required('validation.ePrescriptionIssuePrescription.doctorFirstName')
				.nullable(),
		}),
		doctorLastName: yup.string().when('recommendingDoctorCheck', {
			is: true,
			then: yup
				.string()
				.required('validation.ePrescriptionIssuePrescription.doctorLastName')
				.nullable(),
		}),
		doctorPhoneNumber: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number')
			.when('recommendingDoctorCheck', {
				is: true,
				then: yup
					.number()
					.required(
						'validation.ePrescriptionIssuePrescription.doctorPhoneNumber',
					)
					.nullable(),
			}),
		expertise: yup.mixed().when('recommendingDoctorCheck', {
			is: true,
			then: yup
				.object({
					label: yup.string(),
					value: yup.string(),
				})
				.required('validation.ePrescriptionIssuePrescription.expertise')
				.nullable(),
		}),
		ic: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number')
			.when('recommendingDoctorCheck', {
				is: true,
				then: yup
					.number()
					.required('validation.ePrescriptionIssuePrescription.ic')
					.nullable(),
			}),
		icp: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number')
			.when('recommendingDoctorCheck', {
				is: true,
				then: yup
					.number()
					.required('validation.ePrescriptionIssuePrescription.icp')
					.nullable(),
			}),
		icz: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number')
			.when('recommendingDoctorCheck', {
				is: true,
				then: yup
					.number()
					.required('validation.ePrescriptionIssuePrescription.icz')
					.nullable(),
			}),
		dic: yup
			.string()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number')
			.when('recommendingDoctorCheck', {
				is: true,
				then: yup
					.string()
					.required('validation.ePrescriptionIssuePrescription.dic')
					.nullable(),
			}),
		providerName: yup.string().when('recommendingDoctorCheck', {
			is: true,
			then: yup
				.string()
				.required('validation.ePrescriptionIssuePrescription.providerName')
				.nullable(),
		}),
		note: yup.string().max(1000, 'base.error.noteFormatError').notRequired(),
		notification: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		notificationPhoneNumber: yup
			.string()
			.when('notification', {
				is: (notification: SelectValueProps) => notification.value === 'sms',
				then: yup
					.string()
					.required(
						'validation.ePrescriptionIssuePrescription.notificationPhoneNumber',
					)
					.nullable(),
			})
			.nullable(),
		notificationEmail: yup
			.string()
			.when('notification', {
				is: (notification: SelectValueProps) => notification.value === 'email',
				then: yup.string().email().required().nullable(),
			})
			.nullable(),
		recommendingDoctorCheck: yup.boolean().notRequired(),
		international: yup.boolean().notRequired(),
		repeatAbleCheck: yup.boolean().notRequired(),
		repeating: yup.mixed().when('repeatAbleCheck', {
			is: (repeatAbleCheck: boolean) => repeatAbleCheck === true,
			then: yup
				.number()
				.required('validation.ePrescription.repeating')
				.typeError('validation.ePrescription.number'),
		}),
		validTo: yup
			.date()
			.required('validation.ePrescription.validTo')
			.min(new Date(new Date().setHours(0, 0, 0, 0)))
			.nullable(),
		workplacePhoneNumber: !workplaceData?.workplace?.phone
			? yup
					.string()
					.required(
						'validation.ePrescriptionIssuePrescription.workplacePhoneNumber',
					)
					.nullable()
			: yup.string().nullable().notRequired(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		registerFormByKey(
			TreatmentFormModalContextKeys.PRESCRIPTION_STEP_3,
			() => formMethods,
		);
	}, []);

	return (
		<ScrollContainer className={styles.wrapper}>
			<div className='row'>
				<div className='col-lg-6'>
					<FormWrapper formMethods={formMethods}>
						<Text
							tx='component.ePrescription.prescription'
							className={styles.title}
							firstUpper
						/>
						<FormControl
							label='component.ePrescription.expirationDate'
							name='validTo'
							isRequired>
							<DatePicker minDate={moment()} />
						</FormControl>
						<div className='row'>
							<div className='col-md-6'>
								<FormControl name='repeatAbleCheck' hideRequired>
									<Checkbox tx='component.ePrescription.repeating' />
								</FormControl>
							</div>
						</div>
						<FormCondition controlName='repeatAbleCheck' equal={true}>
							<FormControl
								label='component.ePrescription.howManyTimes'
								name='repeating'
								isRequired>
								<TextField />
							</FormControl>
						</FormCondition>
						<div className='row'>
							<div className='col-md-6'>
								<FormControl name='acute' hideRequired>
									<Checkbox tx='component.ePrescription.acuteCare' />
								</FormControl>
							</div>
							<div className='col-md-6'>
								<FormControl name='adUsumProprium' hideRequired>
									<Checkbox tx={'component.ePrescription.adUsumProprium'} />
								</FormControl>
							</div>
						</div>
						<InputWrapper>
							<SelectField
								label='component.ePrescription.receivedIDePrescription'
								name='notification'
								options={ePrescriptionDeliveryOptions}
							/>
						</InputWrapper>
						<FormCondition controlName='notification' equal='sms'>
							<div style={{ marginBottom: '10px' }}>
								<PhoneNumberField
									name='notificationPhoneNumber'
									label='base.shared.phone'
									isRequired
								/>
							</div>
						</FormCondition>
						<FormCondition controlName='notification' equal='email'>
							<FormControl
								label='base.shared.email'
								name='notificationEmail'
								isRequired>
								<TextField />
							</FormControl>
						</FormCondition>
						<FormControl
							label='component.ePrescription.noteForDoctor'
							name='note'>
							<TextField />
						</FormControl>
						{!workplaceData?.workplace?.phone && (
							<div style={{ marginBottom: '10px' }}>
								<PhoneNumberField
									name='workplacePhoneNumber'
									label='component.ePrescription.workplacePhoneNumber'
									isRequired
								/>
							</div>
						)}
						<FormControl name='recommendingDoctorCheck' hideRequired>
							<Checkbox tx='component.ePrescription.recommendingDoctor' />
						</FormControl>
						<FormCondition controlName='recommendingDoctorCheck' equal>
							<Text
								tx={'component.ePrescription.recommendingDoctor'}
								className={styles.title}
								firstUpper
							/>
							<FormControl
								label='component.recommendingDoctor.name'
								name='doctorFirstName'
								isRequired>
								<TextField />
							</FormControl>
							<FormControl
								label='component.recommendingDoctor.surname'
								name='doctorLastName'
								isRequired>
								<TextField />
							</FormControl>
							<FormControl
								label={'component.recommendingDoctor.speciality'}
								name='expertise'
								isRequired>
								<TermItemSelectField
									name='expertise'
									termType='sukl_obor_pece'
									useValue
								/>
							</FormControl>
							<FormControl
								label={'component.recommendingDoctor.nameOfProvider'}
								name='providerName'
								isRequired>
								<TextField />
							</FormControl>
							<div style={{ marginBottom: '10px' }}>
								<PhoneNumberField
									name='doctorPhoneNumber'
									label='component.recommendingDoctor.phone'
									isRequired
								/>
							</div>
							<NumberField
								label='component.recommendingDoctor.icp'
								name='icp'
								isRequired
							/>
							<NumberField
								label='component.recommendingDoctor.icz'
								name='icz'
								isRequired
							/>
							<NumberField
								label='component.recommendingDoctor.ic'
								name='ic'
								isRequired
							/>
							<InputFieldWIthPrefix
								name='dic'
								prefix={'CZ'}
								label='component.recommendingDoctor.dic'
								isRequired
							/>
						</FormCondition>
						<FormControl name='international' hideRequired>
							<Checkbox tx='component.ePrescription.international' />
						</FormControl>
						<div className='d-flex mt-4 justify-content-between'>
							<SubmitButton title='base.create' onSubmit={handleSave} />
						</div>
					</FormWrapper>
				</div>
			</div>
		</ScrollContainer>
	);
};

export default EPrescriptionIssuePrescription;
